import { useEffect, useState } from "react";
import api from "../constant/api";
import { Html5QrcodeScanner } from "html5-qrcode";
import { openDB } from "idb";
import { toast } from "react-toastify";
import { ShoppingCart, Trash2, Play, Square, Clock, X } from "lucide-react";

export default function GarmentsPOS() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(null); // garments_cart session
  const [cartItems, setCartItems] = useState([]); // garments_cart_items list
  const [search, setSearch] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [selectPayment, setSelectPayment] = useState("Cash");
  const [billDate, setBillDate] = useState("");

  const [discount, setDiscount] = useState(0);
  const [gst, setGst] = useState(0);

  // Persistence States
  const [cartId, setCartId] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);

  // Pending Modal States
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingCartsList, setPendingCartsList] = useState([]);

  const selectedBranch = localStorage.getItem("selectedBranch") || "";
  const selectedBranchName = localStorage.getItem("selectedBranchName") || "Ak Ceramic World";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const user_id = user.user_id;

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
    fetchProducts();
    fetchCustomers();
  }, []);

  // RESTORE CART SESSION ON RELOAD FROM LOCALSTORAGE
  useEffect(() => {
    const savedCartId = localStorage.getItem("garmentsCartId");
    if (savedCartId) {
      setCartId(savedCartId);
      setSessionActive(true);
      loadCartDetails(savedCartId);
    }
  }, []);

  const loadCartDetails = async (cId) => {
    try {
      const res = await api.get(`/GarmentsProduct/getCartDetails/${cId}`);
      if (res.data?.success) {
        setCart(res.data.cart);
        setCartItems(res.data.items || []);
        setSelectedCustomer({
          customer_id: res.data.cart.customer_id,
          name: res.data.cart.customer_name,
          phone: res.data.cart.phone
        });
        setCustomerSearch(res.data.cart.customer_name);
        setDiscount(res.data.cart.discount || 0);
        setGst(res.data.cart.gst || 0);
        setSelectPayment(res.data.cart.payment_mode || "Cash");
      }
    } catch (err) {
      localStorage.removeItem("garmentsCartId");
      setCartId(null);
      setSessionActive(false);
    }
  };

  const dbPromise = openDB("ak-garments-pos-db", 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("garment_products")) {
        db.createObjectStore("garment_products", { keyPath: "garment_product_id" });
      }
    }
  });

  const fetchProducts = async () => {
    try {
      const res = await api.get("/GarmentsProduct/getProducts");
      const data = res.data.data || [];
      setProducts(data);

      const db = await dbPromise;
      const tx = db.transaction("garment_products", "readwrite");
      data.forEach(p => tx.store.put(p));
      await tx.done;
    } catch (err) {
      const db = await dbPromise;
      const data = await db.getAll("garment_products");
      setProducts(data);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get("/Product/getCustomers");
      setCustomers(res.data.data || []);
    } catch (err) {}
  };

  // 1. START CART SESSION (Default pending = 0)
  const handleStartCart = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer first! ❌");
      return;
    }

    try {
      const res = await api.post("/GarmentsProduct/createCart", {
        customer_id: selectedCustomer.customer_id,
        customer_name: selectedCustomer.name,
        phone: selectedCustomer.phone,
        branch: selectedBranch,
        salesman_id: user_id,
        pending: 0,
      });

      if (res.data?.success) {
        const newCart = res.data.cart;
        setCart(newCart);
        setCartId(newCart.cart_id);
        setSessionActive(true);
        localStorage.setItem("garmentsCartId", newCart.cart_id);
        setCartItems([]);
        toast.success("Cart Session Started! 🚀");
      }
    } catch (err) {
      toast.error("Failed to start cart session");
    }
  };

  // 2. END / CLEAR CART SESSION
  const handleEndCart = async () => {
    if (cartItems.length > 0 && !window.confirm("Are you sure you want to discard this cart?")) return;
    
    try {
      if (cart || cartId) {
        await api.post("/GarmentsProduct/deleteCart", { cart_id: cart?.cart_id || cartId });
      }
    } catch (e) {}

    setCart(null);
    setCartId(null);
    setSessionActive(false);
    localStorage.removeItem("garmentsCartId");
    setCartItems([]);
    setSelectedCustomer(null);
    setCustomerSearch("");
    setDiscount(0);
    setGst(0);
    toast.info("Cart session cleared.");
  };

  // 3. ADD ITEM TO CART
  const handleAddToCart = async (product, variant) => {
    if (!cart && !cartId) {
      toast.error("Please click 'Start Order' first! ❌");
      return;
    }

    try {
      const res = await api.post("/GarmentsProduct/addItem", {
        cart_id: cart?.cart_id || cartId,
        garment_product_id: product.garment_product_id,
        variant_id: variant.variant_id,
        product_name: product.product_name,
        size: variant.size,
        color: variant.color,
        sku: variant.sku,
        qty: 1,
        rate: product.price,
      });

      if (res.data?.success) {
        setCartItems(res.data.items || []);
        toast.success("Item added to cart! ✅");
      }
    } catch (err) {
      toast.error("Failed to add item");
    }
  };

  // 4. UPDATE CART ITEM QTY OR RATE
  const handleUpdateItem = async (item_id, field, value) => {
    const val = Number(value);
    if (val < 0) return;

    try {
      const res = await api.post("/GarmentsProduct/updateItem", {
        cart_id: cart?.cart_id || cartId,
        item_id: item_id,
        field: field,
        value: val,
      });

      if (res.data?.success) {
        setCartItems(res.data.items || []);
      }
    } catch (err) {
      toast.error("Failed to update item");
    }
  };

  // 5. REMOVE ITEM FROM CART
  const handleRemoveItem = async (item_id) => {
    if (!window.confirm("Remove this item?")) return;

    try {
      const res = await api.post("/GarmentsProduct/removeItem", {
        cart_id: cart?.cart_id || cartId,
        item_id: item_id,
      });

      if (res.data?.success) {
        setCartItems(res.data.items || []);
        toast.info("Item removed");
      }
    } catch (err) {
      toast.error("Failed to remove item");
    }
  };

  const subTotal = cartItems.reduce((sum, item) => sum + Number(item.total || 0), 0);
  const netTotal = subTotal - Number(discount || 0) + Number(gst || 0);

  // 6. CONVERT CART TO ORDER (Final Checkout)
  const handleConvertToOrder = async () => {
    const currentCartId = cart?.cart_id || cartId;
    if (!currentCartId) {
      toast.error("No active cart found! ❌");
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Cart is empty! ❌");
      return;
    }

    try {
      const res = await api.post("/GarmentsProduct/convertToOrder", {
        cart_id: currentCartId,
        customer_name: selectedCustomer?.name || cart?.customer_name,
        phone: selectedCustomer?.phone || cart?.phone,
        gst_no: selectedCustomer?.gst_no || "",
        address: selectedCustomer?.address || "",
        date: billDate,
        branch: selectedBranch,
        salesman_id: user_id,
        total: subTotal,
        discount: Number(discount || 0),
        gst: Number(gst || 0),
        net_total: netTotal,
        payment_mode: selectPayment,
        status: "Completed",
        pending: 0,
      });

      if (res.data?.success) {
        toast.success(res.data.message || `Order Saved Successfully! ✅`);
        handleEndCart();
        fetchProducts();
      }
    } catch (err) {
      toast.error("Failed to convert cart to order ❌");
    }
  };

  // 7. SAVE AS PENDING ORDER (Sets pending = 1 without order conversion)
  const handleSavePending = async () => {
    const currentCartId = cart?.cart_id || cartId;
    if (!currentCartId) {
      toast.error("No active cart found! ❌");
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Cart is empty! ❌");
      return;
    }

    try {
      const res = await api.post("/GarmentsProduct/saveAsPending", {
        cart_id: currentCartId,
        total: subTotal,
        discount: Number(discount || 0),
        gst: Number(gst || 0),
        net_total: netTotal,
        payment_mode: selectPayment,
        pending: 1,
      });

      if (res.data?.success) {
        toast.success("Cart Saved as Pending Order! ⏳");
        setCart(null);
        setCartId(null);
        setSessionActive(false);
        localStorage.removeItem("garmentsCartId");
        setCartItems([]);
        setSelectedCustomer(null);
        setCustomerSearch("");
        setDiscount(0);
        setGst(0);
      }
    } catch (err) {
      toast.error("Failed to save as pending order ❌");
    }
  };

  // Fetch Pending Carts
  const fetchPendingCarts = async () => {
    try {
      const res = await api.get("/GarmentsProduct/getPendingCarts");
      if (res.data?.success) {
        setPendingCartsList(res.data.data || []);
        setShowPendingModal(true);
      }
    } catch (err) {
      toast.error("Failed to load pending orders");
    }
  };

  // Load Selected Pending Cart into POS (changes pending back to 0)
  const handleLoadPendingCart = async (cartObj) => {
    try {
      await api.post("/GarmentsProduct/updateCartPending", { cart_id: cartObj.cart_id, pending: 0 });

      const res = await api.get(`/GarmentsProduct/getCartDetails/${cartObj.cart_id}`);
      if (res.data?.success) {
        setCart(res.data.cart);
        setCartId(cartObj.cart_id);
        setSessionActive(true);
        localStorage.setItem("garmentsCartId", cartObj.cart_id);
        setCartItems(res.data.items || []);
        setSelectedCustomer({
          customer_id: res.data.cart.customer_id,
          name: res.data.cart.customer_name,
          phone: res.data.cart.phone
        });
        setCustomerSearch(res.data.cart.customer_name);
        setDiscount(res.data.cart.discount || 0);
        setGst(res.data.cart.gst || 0);
        setSelectPayment(res.data.cart.payment_mode || "Cash");
        setShowPendingModal(false);
        toast.success(`Pending Cart #${cartObj.cart_id} Loaded Successfully! 🚀`);
      }
    } catch (err) {
      toast.error("Failed to load cart details");
    }
  };

  // Scanner integration
  useEffect(() => {
    if (!showScanner || (!cart && !cartId)) return;
    let isScanning = false;

    const scanner = new Html5QrcodeScanner("qr-reader", { fps: 10, qrbox: 250 }, false);
    const beep = new Audio("https://actions.google.com/sounds/v1/alarms/beep_short.ogg");

    scanner.render(
      async (decodedText) => {
        if (isScanning) return;
        isScanning = true;

        let foundProduct = null;
        let foundVariant = null;

        for (const p of products) {
          if (p.product_code === decodedText) {
            foundProduct = p;
            foundVariant = p.variants?.[0];
            break;
          }
          const vMatch = p.variants?.find(v => v.sku === decodedText);
          if (vMatch) {
            foundProduct = p;
            foundVariant = vMatch;
            break;
          }
        }

        if (!foundProduct || !foundVariant) {
          isScanning = false;
          return;
        }

        handleAddToCart(foundProduct, foundVariant);
        beep.play();
        if (navigator.vibrate) navigator.vibrate(100);

        setTimeout(() => {
          isScanning = false;
        }, 3000);
      },
      () => {}
    );

    return () => {
      scanner.clear().catch(() => {});
    };
  }, [showScanner, products, cart, cartId]);

  const filteredProducts = products.filter(p =>
    p.product_name.toLowerCase().includes(search.toLowerCase()) ||
    p.product_code?.includes(search)
  );

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch)
  );

  return (
    <div className="bg-slate-100 min-h-screen p-2 md:p-4 space-y-3">
      {/* TOP BAR */}
      <div className="bg-indigo-600 text-white p-3 rounded-2xl flex flex-col md:flex-row justify-between gap-2 shadow-sm items-center">
        <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
          <input
            type="date"
            value={billDate}
            onChange={(e) => setBillDate(e.target.value)}
            className="text-black px-2.5 py-1.5 rounded-xl text-xs font-semibold bg-white outline-none"
          />

          <select 
            value={selectPayment}
            onChange={(e) => setSelectPayment(e.target.value)}
            className="text-black px-2.5 py-1.5 rounded-xl text-xs font-semibold bg-white outline-none"
          >
            <option>Cash</option>
            <option>UPI</option>
            <option>Card</option>
          </select>

          {!sessionActive ? (
            <button
              onClick={handleStartCart}
              className="bg-amber-500 hover:bg-amber-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold shadow flex items-center gap-1.5"
            >
              <Play size={13} /> Start Order
            </button>
          ) : (
            <button
              onClick={handleEndCart}
              className="bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold shadow flex items-center gap-1.5"
            >
              <Square size={13} /> End / Clear Cart
            </button>
          )}

          <button
            onClick={fetchPendingCarts}
            className="bg-amber-600 hover:bg-amber-700 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold shadow flex items-center gap-1.5"
          >
            <Clock size={13} /> Pending Orders
          </button>
        </div>

        <div className="text-center md:text-right">
          <h1 className="font-bold text-xs md:text-sm">{selectedBranchName} (Garments POS)</h1>
          {sessionActive && <span className="text-[10px] bg-indigo-500 px-2 py-0.5 rounded-full text-yellow-200">Cart ID: #{cartId}</span>}
        </div>
      </div>

      {/* SEARCH & CUSTOMER SECTION */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white p-3.5 rounded-2xl shadow-sm relative space-y-2">
          <label className="text-xs font-bold text-slate-700">Customer Details</label>
          <input
            placeholder="Search Customer..."
            value={customerSearch}
            onChange={(e) => {
              setCustomerSearch(e.target.value);
              setShowCustomerList(true);
            }}
            disabled={sessionActive}
            className="border p-2.5 w-full rounded-xl text-xs font-semibold outline-none focus:border-indigo-500 disabled:opacity-60"
          />

          {showCustomerList && !sessionActive && customerSearch && (
            <div className="absolute bg-white border w-full max-h-40 overflow-auto z-50 rounded-xl shadow-lg mt-1 divide-y">
              {filteredCustomers.map(c => (
                <div
                  key={c.customer_id}
                  onClick={() => {
                    setSelectedCustomer(c);
                    setCustomerSearch(c.name);
                    setShowCustomerList(false);
                  }}
                  className="p-2.5 hover:bg-slate-50 cursor-pointer text-xs"
                >
                  <p className="font-bold text-slate-800">{c.name}</p>
                  <p className="text-[11px] text-slate-500">{c.phone}</p>
                </div>
              ))}
            </div>
          )}

          {selectedCustomer && (
            <div className="bg-indigo-50/50 border border-indigo-100 p-2.5 rounded-xl text-xs space-y-0.5">
              <p className="font-bold text-indigo-900">{selectedCustomer.name}</p>
              <p className="text-slate-600">{selectedCustomer.phone}</p>
            </div>
          )}
        </div>

        <div className="md:col-span-2 bg-white p-3.5 rounded-2xl shadow-sm relative space-y-2">
          <label className="text-xs font-bold text-slate-700">Add Items to Cart</label>
          <input
            placeholder="Search Garment Product / Code..."
            disabled={!sessionActive}
            className="w-full border p-2.5 rounded-xl text-xs font-semibold outline-none focus:border-indigo-500 disabled:bg-slate-100"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {search && sessionActive && (
            <div className="absolute bg-white border w-full max-h-60 overflow-auto z-50 rounded-xl shadow-lg mt-1 p-2 space-y-2">
              {filteredProducts.map(p => (
                <div key={p.garment_product_id} className="p-2 border-b">
                  <p className="font-bold text-xs text-slate-800">{p.product_name} - ₹{p.price}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.variants?.map(v => (
                      <button
                        key={v.variant_id}
                        onClick={() => {
                          handleAddToCart(p, v);
                          setSearch("");
                        }}
                        className="bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white px-2 py-1 rounded-lg text-[11px] font-bold transition"
                      >
                        {v.size} / {v.color} (Stock: {v.qty})
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setShowScanner(!showScanner)}
            disabled={!sessionActive}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full py-2 rounded-xl text-xs font-bold disabled:opacity-50"
          >
            📷 Barcode / QR Scanner
          </button>
          {showScanner && <div id="qr-reader" className="mt-2" />}
        </div>
      </div>

      {/* CART ITEMS TABLE & CARDS */}
      <div className="bg-white rounded-2xl shadow-sm p-3.5 space-y-3">
        <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
          <div className="flex items-center gap-2">
            <ShoppingCart size={16} className="text-indigo-600" />
            <h2 className="text-xs font-bold text-slate-900 uppercase">Garments Cart Items Table</h2>
          </div>
          <span className="bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-full text-xs font-bold">
            {cartItems.length} Items
          </span>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
              <tr>
                <th className="p-2.5 text-left font-semibold">Item Name</th>
                <th className="p-2.5 font-semibold">Variant (Size / Color)</th>
                <th className="p-2.5 font-semibold text-center">Quantity</th>
                <th className="p-2.5 font-semibold">Rate (₹)</th>
                <th className="p-2.5 font-semibold text-right">Total (₹)</th>
                <th className="p-2.5 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {cartItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-6 text-slate-400">
                    {sessionActive ? "Cart is empty. Add items to bill." : "Click 'Start Order' to begin billing."}
                  </td>
                </tr>
              ) : (
                cartItems.map(item => (
                  <tr key={item.item_id} className="hover:bg-slate-50/50">
                    <td className="p-2.5 font-bold text-slate-900">{item.product_name}</td>
                    <td className="p-2.5 text-slate-500 font-medium">{item.size} / {item.color}</td>
                    <td className="p-2.5 text-center">
                      <input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) => handleUpdateItem(item.item_id, "qty", e.target.value)}
                        className="w-16 border rounded-xl text-center py-1 font-bold text-xs outline-none"
                      />
                    </td>
                    <td className="p-2.5">
                      <input
                        type="number"
                        min="0"
                        value={item.rate}
                        onChange={(e) => handleUpdateItem(item.item_id, "rate", e.target.value)}
                        className="w-20 border rounded-xl text-center py-1 font-bold text-xs outline-none"
                      />
                    </td>
                    <td className="p-2.5 text-right font-extrabold text-slate-900">
                      ₹{Number(item.total || 0).toFixed(2)}
                    </td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => handleRemoveItem(item.item_id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg font-bold">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2.5">
          {cartItems.length === 0 ? (
            <div className="text-center p-6 text-slate-400 text-xs">
              {sessionActive ? "Cart is empty. Add items to bill." : "Click 'Start Order' to begin billing."}
            </div>
          ) : (
            cartItems.map(item => (
              <div key={item.item_id} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-xs text-slate-900">{item.product_name}</p>
                    <p className="text-[11px] text-slate-500">Variant: {item.size} / {item.color}</p>
                  </div>
                  <span className="font-extrabold text-xs text-slate-900">₹{Number(item.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">Qty:</span>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) => handleUpdateItem(item.item_id, "qty", e.target.value)}
                      className="w-14 border rounded-lg text-center py-1 font-bold text-xs bg-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">Rate:</span>
                    <input
                      type="number"
                      min="0"
                      value={item.rate}
                      onChange={(e) => handleUpdateItem(item.item_id, "rate", e.target.value)}
                      className="w-16 border rounded-lg text-center py-1 font-bold text-xs bg-white"
                    />
                  </div>
                  <button onClick={() => handleRemoveItem(item.item_id)} className="text-rose-600 p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* SUMMARY & CHECKOUT */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white p-3.5 rounded-2xl shadow-sm flex flex-col justify-between space-y-2">
          <h3 className="text-xs font-bold text-slate-700 uppercase">Adjustments</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-slate-500 font-semibold">Discount (₹)</label>
              <input
                type="number"
                min="0"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="border w-full rounded-xl p-1.5 text-xs font-bold outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 font-semibold">GST (₹)</label>
              <input
                type="number"
                min="0"
                value={gst}
                onChange={(e) => setGst(e.target.value)}
                className="border w-full rounded-xl p-1.5 text-xs font-bold outline-none"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-3.5 rounded-2xl shadow-sm space-y-2 flex flex-col justify-between">
          <div className="space-y-1 text-xs">
            <div className="flex justify-between text-slate-500">
              <span>Subtotal:</span>
              <span className="font-bold text-slate-800">₹{subTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-extrabold text-slate-900 border-t pt-1.5">
              <span>Net Total:</span>
              <span className="text-emerald-600">₹{netTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-3.5 rounded-2xl shadow-sm flex flex-col justify-center gap-2">
          <button 
            onClick={handleConvertToOrder} 
            disabled={!sessionActive || cartItems.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full py-2.5 rounded-xl text-xs font-bold shadow-sm disabled:opacity-50 transition"
          >
            CONVERT TO ORDER & SAVE ✅
          </button>
          <button 
            onClick={handleSavePending} 
            disabled={!sessionActive || cartItems.length === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white w-full py-2.5 rounded-xl text-xs font-bold shadow-sm disabled:opacity-50 transition"
          >
            SAVE AS PENDING ORDER ⏳
          </button>
        </div>
      </div>

      {/* PENDING ORDERS MODAL */}
      {showPendingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl bg-white p-5 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-sm font-bold text-slate-900">Pending Orders List</h3>
              <button onClick={() => setShowPendingModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-2">
              {pendingCartsList.length === 0 ? (
                <p className="text-center text-xs text-slate-400 py-6">No pending orders found.</p>
              ) : (
                pendingCartsList.map((pc) => (
                  <div key={pc.cart_id} className="flex items-center justify-between p-3 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-indigo-50/50 transition">
                    <div>
                      <p className="text-xs font-bold text-slate-900">Cart ID: #{pc.cart_id} - {pc.customer_name}</p>
                      <p className="text-[11px] text-slate-500">Phone: {pc.phone || "N/A"} | Net: ₹{pc.net_total || 0}</p>
                    </div>
                    <button
                      onClick={() => handleLoadPendingCart(pc)}
                      className="rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 transition"
                    >
                      Load Cart
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}